/**
 * TypeScript interface for AI Tools Collection schema
 * Generated from aiforme.schema.json
 */

import { ItemModel } from "lib/services/catalogical-models";

// Type definitions for common formats
type ISODateTimeFormat = string; // Format: date-time
type URIFormat = string; // Format: uri

/**
 * User rating object structure
 */
interface UserRating {
  /** Username of the user who submitted the rating */
  username: string;

  /** Rating given by this user (1-5 scale) */
  rating: number;

  /** When the rating was submitted */
  timestamp: ISODateTimeFormat;
}

/**
 * Approval status options
 */
export type ApprovalStatus = "APPROVED" | "PENDING" | "REJECTED" | "UPDATED";

export const JOB_FAMILIES = [
  "All",
  ...[
    "Administrative Support",
    "Applied Science",
    "Business Intel Engineer",
    "Category Manager",
    "Customer Success Manager",
    "Design Technology Engineer",
    "Enterprise On-Ramp",
    "Enterprise Support",
    "HR",
    "Legal",
    "Marketing",
    "Network & Infrastructure",
    "Operations",
    "Product Management",
    "Program Management",
    "PXT",
    "Retail",
    "Risk Family",
    "Sales/Acct Management",
    "Security",
    "Software Development",
    "Supply Chain Management",
    "Tax",
    "Technical (general)",
    "Technical Program Management",
    "Training/Development",
    "UX Designer",
  ].sort(),
];

export const DATA_CLASSIFICATION = ["Public", "Business Data", "Customer Data", "Prohibited"];

export const CATEGORIES = [
  "Recommended",
  ...[
    "AI Chatbot",
    "Category Management",
    "Communication",
    "Customer Management",
    "Data & Analysis",
    "Design & Creative",
    "Document Management",
    "Financial Tools",
    "Knowledge",
    "Learning",
    "Legal Management",
    "Media & Entertainment",
    "Operational Excellence",
    "Organization",
    "People & Talent",
    "Presentation",
    "Process Automation",
    "Project Management",
    "Quality Assurance",
    "Resource Planning",
    "Security",
    "Software development",
    "Sustainability",
    "Time Management",
    "Vendor Management",
  ].sort(),
];

export const SORTING_PRIORITY = [
  { label: "Recommended tools", value: 1 },
  { label: "Supported tools", value: 2 },
  { label: "Under assessment", value: 3 },
];

export const COMPARE_TABLE_RECOMMENDATION_TYPE_LABELS = [
  { sortingPriorityValue: 1, recommendationTypeUiStringPath: "aiForMe.compare.table.recommendation.types.recommended" },
  { sortingPriorityValue: 2, recommendationTypeUiStringPath: "aiForMe.compare.table.recommendation.types.supported" },
  {
    sortingPriorityValue: 3,
    recommendationTypeUiStringPath: "aiForMe.compare.table.recommendation.types.underAssessment",
  },
];

export type SortOption = {
  label: string;
  field: string[];
  order?: ("asc" | "desc")[];
};

export const sortingOptions: SortOption[] = [
  {
    label: "Best Match",
    field: [],
    order: [],
  },
  { label: "Highest Rated", field: ["feedback.averageRating", "feedback.numberOfRatings"], order: ["desc"] },
  { label: "Most Reviewed", field: ["feedback.numberOfRatings", "feedback.averageRating"], order: ["desc"] },
  { label: "Most Viewed", field: ["catalogicalGenerated.interactions.clicks"], order: ["desc"] },
  { label: "Newest", field: ["creationDate"], order: ["desc"] },
  { label: "Tools A-Z", field: ["title"], order: ["asc"] },
  { label: "Tools Z-A", field: ["title"], order: ["desc"] },
  { label: "No sorting", field: [], order: [] },
];

/**
 * Main AIForMe interface
 */
export interface AIForMeTool extends ItemModel {
  /** Current approval status of the tool */
  status: ApprovalStatus;

  /** List of job families that would benefit from this tool */
  jobFamilies: string[];

  /** Date when the tool entry was created (ISO format) */
  creationDate: ISODateTimeFormat;

  /** List of categories that the tool belongs to */
  categories: string[];

  /** URL to additional information about the tool */
  learnMoreLink: URIFormat;

  /** Detailed description of the tool and its capabilities */
  description: string;

  /** List of tags associated with the tool */
  tags: string[];

  /** Name of the tool */
  title: string;

  /** Optional display name set by catalog managers; takes precedence over title when present */
  displayName?: string;

  /** Direct URL to access the tool */
  directToolUrl?: URIFormat;

  /** Documentation URL for custom built apps */
  documentationUrl?: URIFormat;

  /** Brief subtitle or tagline for the tool */
  subtitle?: string;

  /** Label for the secondary action button */
  secondaryAction?: string;

  /** Label for the primary action button */
  primaryAction?: string;

  /** Version number of the tool entry */
  version?: number;

  /** Username of the person who submitted the tool */
  username?: string;

  /** Collection of individual user ratings for this tool */
  ratings?: UserRating[];

  recommended: boolean;

  dataClassification: string;

  /** Tool maturity level */
  toolMaturity?: string;

  /** Base Platform for a tool */
  basePlatform?: string;

  sortingPriority?: number;

  securityReviewed?: string;

  /** Security review link (ASR or Talos) */
  securityReviewLink?: string;

  /** Type of security review link (ASR or Talos) */
  securityReviewSystem?: string;

  /** Selected AI models used by the tool */
  modelsUsed?: string[];

  /** Other model name if not in the standard list */
  otherModel?: string;

  /** Whether the tool stores input prompts */
  storesInputPrompts?: string;

  /** Whether the tool uses data for retraining */
  collectsDataForRetraining?: string;

  /** Tool bindle identifier */
  toolBindle?: string;

  /** CTI (Change and Test Information) link */
  toolCTILink?: string;

  /** Business justification for third-party tools */
  businessJustification?: string;

  /** Base Platform Metadata Taxonomy ID for tools with recognized base platforms */
  basePlatformMetadataTaxonomyId?: string;
}

export interface UseCaseModel extends ItemModel {
  /** Domain assigned for the use case */
  applicableDomain: string;

  /** The assigned name for the use case */
  name: string;

  /** List of job families recommended for the use case */
  recommendedJobFamilies?: string[];

  /** List of tool ids associated with the use case */
  associatedToolIds?: string[];
}

export type EnumOption = {
  label: string;
  value: any;
};

export interface DataDomainMetadata {
  /**
   * Dictionary mapping enum names to their possible values
   */
  enums: Record<string, EnumOption[]>;
}
